import React, { useState } from 'react';
import { CheckCircle, Box } from 'lucide-react';
import { Recommendation } from '../../types';
import { getToolLogoUrl, getBrandColor } from '../../utils/tools/logos';

interface VisibleToolCardProps {
  recommendation: Recommendation;
}

export function VisibleToolCard({ recommendation }: VisibleToolCardProps) {
  const { tool, reasons } = recommendation;
  const [logoError, setLogoError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const logoUrl = getToolLogoUrl(tool);
  const brandColor = getBrandColor(tool.id);

  const handleLogoError = () => {
    if (retryCount < 1 && tool.website) {
      const domain = tool.website.replace(/^https?:\/\//, '').replace('www.', '').split('/')[0];
      const img = document.createElement('img');
      img.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
      setRetryCount(1);
    } else {
      setLogoError(true);
    }
  };

  const getFallbackLogoUrl = () => {
    if (tool.website) {
      const domain = tool.website.replace(/^https?:\/\//, '').replace('www.', '').split('/')[0];
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    }
    return logoUrl;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
            style={{ backgroundColor: logoError ? `#${brandColor}15` : '#f8fafc' }}
          >
            {!logoError ? (
              <img
                src={retryCount > 0 ? getFallbackLogoUrl() : logoUrl}
                alt={`${tool.name} logo`}
                className="w-10 h-10 object-contain"
                onError={handleLogoError}
              />
            ) : (
              <Box
                className="w-6 h-6"
                style={{ color: `#${brandColor}` }}
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-gray-900">
                {tool.name}
              </h3>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                Top Pick
              </span>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              {tool.category}
            </p>
            {tool.description && (
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {tool.description}
              </p>
            )}
            {reasons && reasons.length > 0 && (
              <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{reasons[0]}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
